import { useEffect, useMemo, useRef, useState } from 'react';
import { elementColor } from '../elements';
import {
  cueHighlights,
  cueIsActive,
  NowYouTry,
  usePrefersReducedMotion,
  useMountSequence,
  VizCard,
  type VizCue,
} from '../vizShared';

// ConcentrationView — a beaker of solution that makes molarity tangible:
//   M = moles of solute ÷ liters of solution.
// Solute particles are dispersed through the solvent at a density proportional
// to the concentration; a live readout shows M, moles and volume, and a slider
// sweeps concentrated ↔ dilute so the particle density changes before your eyes.
// Moles and volume are linked to molarity (M = n / V), so dragging the slider
// keeps the chemistry honest: at fixed volume, fewer particles ⇒ lower M.
//
// config: { molarity?, solute?, volume? (L), title? }
//   Resilient: empty config → 1.0 M NaCl in 1.0 L.
// Cue: cue.progress / cue.step sweep concentrated → dilute as the narration
//   describes the dilution; cue.highlight ("solute"/symbol) spotlights the
//   dissolved particles.

export interface ConcentrationViewConfig {
  title?: string;
  /** Concentration in mol/L. */
  molarity?: number;
  /** Solute formula/symbol shown on the particles (e.g. "NaCl", "Cu"). */
  solute?: string;
  /** Solution volume in liters. */
  volume?: number;
}

interface Props {
  config?: ConcentrationViewConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

// Molarity range the slider sweeps (concentrated ↔ dilute). 0 is "pure solvent".
const M_MIN = 0;
const M_MAX = 2.0;
const M_STEP = 0.05;

// Particle field geometry. The beaker interior is a fixed box; we scatter up to
// MAX_DOTS solute particles into it on a jittered grid, then show a count
// proportional to molarity. At M_MAX every cell is filled (densest packing we
// draw); at 0 the solvent is clear.
const MAX_DOTS = 120;

// Beaker SVG viewBox.
const W = 200;
const H = 220;
// Interior (where solution sits): inset from the glass walls.
const IN_X = 30;
const IN_W = 140;
const IN_TOP = 40;
const IN_BOTTOM = 196;

function clampM(x: number): number {
  if (!Number.isFinite(x)) return 1;
  return Math.min(M_MAX, Math.max(M_MIN, x));
}

function fmt(x: number): string {
  if (!Number.isFinite(x)) return '—';
  if (x === 0) return '0';
  if (Math.abs(x) >= 100) return x.toFixed(0);
  if (Math.abs(x) >= 10) return x.toFixed(1);
  if (Math.abs(x) >= 1) return x.toFixed(2);
  return x.toFixed(3);
}

// A stable scatter of candidate particle positions inside the solution box. We
// generate the full MAX_DOTS field once (jittered grid so it looks dissolved,
// not gridded) and reveal a prefix of it by concentration — so raising M adds
// particles in place rather than reshuffling everything each tick.
function buildField(seedKey: string): Array<{ x: number; y: number; r: number }> {
  // Tiny deterministic PRNG so the field is stable across re-renders but varies
  // by solute, and never depends on Math.random at render time.
  let s = 0;
  for (let i = 0; i < seedKey.length; i++) s = (s * 31 + seedKey.charCodeAt(i)) >>> 0;
  const rng = () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
  const cols = 10;
  const rows = Math.ceil(MAX_DOTS / cols);
  const cellW = IN_W / cols;
  const cellH = (IN_BOTTOM - IN_TOP) / rows;
  const out: Array<{ x: number; y: number; r: number }> = [];
  for (let i = 0; i < MAX_DOTS; i++) {
    const c = i % cols;
    const r = Math.floor(i / cols);
    const jx = (rng() - 0.5) * cellW * 0.8;
    const jy = (rng() - 0.5) * cellH * 0.8;
    out.push({
      x: IN_X + cellW * (c + 0.5) + jx,
      y: IN_TOP + cellH * (r + 0.5) + jy,
      r: 3.1 + rng() * 1.3,
    });
  }
  // Interleave reveal order so a low concentration disperses evenly through the
  // whole solution instead of clustering at the top.
  return out
    .map((p, i) => ({ p, k: ((i * 2654435761) >>> 0) }))
    .sort((a, b) => a.k - b.k)
    .map((e) => e.p);
}

export default function ConcentrationView({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);

  const solute = (config.solute ?? 'NaCl').trim() || 'NaCl';
  const volume = useMemo(() => {
    const v = config.volume;
    if (!Number.isFinite(v as number) || (v as number) <= 0) return 1;
    return Math.min(5, Math.max(0.05, v as number));
  }, [config.volume]);

  const [molarity, setMolarity] = useState<number>(clampM(config.molarity ?? 1));
  const [spotlight, setSpotlight] = useState(false);
  const interactedRef = useRef(false);

  // Color for the solute particles + the tint of the solution (alpha ∝ M).
  const soluteColor = useMemo(() => {
    // Use the first element symbol of the solute for a sensible CPK-ish color.
    const m = solute.match(/[A-Z][a-z]?/);
    return elementColor(m ? m[0] : solute);
  }, [solute]);

  const field = useMemo(() => buildField(solute), [solute]);

  const mark = () => {
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.({ type: 'concentration_view', molarity, solute, volume });
    }
  };

  // ---- Self-demo: pour in solute (rise to a concentrated value), then dilute
  // down so the learner sees the particle density both grow and thin out.
  const settleM = clampM(config.molarity ?? 1);
  const demo = useMountSequence(
    [
      { at: 150, run: () => setMolarity(0.15) },
      { at: 520, run: () => setMolarity(M_MAX) },
      { at: 1000, run: () => setMolarity(1.2) },
      { at: 1400, run: () => setMolarity(0.4) },
      { at: 1850, run: () => setMolarity(settleM) },
    ],
    { skip: reduced || hasCue, onSettle: () => setMolarity(settleM) },
  );
  const cancelDemo = () => { if (demo.running) demo.cancel(); };

  // ---- Cue: progress / step sweep concentrated → dilute (high M at the start
  // of the beat, low M at the end). highlight spotlights the solute particles.
  useEffect(() => {
    if (cue?.progress != null) {
      const p = Math.max(0, Math.min(1, cue.progress));
      // progress 0 = concentrated (M_MAX), 1 = dilute (near 0).
      setMolarity(clampM(M_MAX * (1 - p)));
    } else if (cue?.step != null) {
      // Discrete steps: 0,1,2,3 → concentrated, ¾, ½, dilute.
      const idx = Math.max(0, Math.min(3, Math.round(cue.step)));
      setMolarity(clampM(M_MAX * (1 - idx / 3)));
    }
  }, [cue?.progress, cue?.step]);

  useEffect(() => {
    const hi = cueHighlights(cue);
    setSpotlight(hi.includes('solute') || hi.includes(solute.toLowerCase()));
  }, [cue, solute]);

  // Derived chemistry. moles = M × V; visible particle count ∝ M.
  const moles = molarity * volume;
  const visible = Math.round((clampM(molarity) / M_MAX) * MAX_DOTS);
  const tintAlpha = Math.min(0.34, (molarity / M_MAX) * 0.34);

  const transition = reduced ? undefined : 'opacity 240ms ease-out, fill-opacity 240ms ease-out';

  // A descriptive word for the current concentration (for the readout + a11y).
  const descriptor = molarity <= 0.001
    ? 'pure solvent'
    : molarity < 0.4
      ? 'dilute'
      : molarity < 1.2
        ? 'moderate'
        : 'concentrated';

  return (
    <VizCard
      reducedMotion={reduced}
      eyebrow="Solutions · Molarity"
      title={config.title ?? 'Concentration (Molarity)'}
      subtitle="Particle density ∝ concentration — diluting spreads the same idea thinner"
      hint="Drag the slider concentrated ↔ dilute · ←/→ to adjust · auto-demo on load"
      actions={<span className="font-mono text-sm font-semibold text-ws-accent">M = n ÷ V</span>}
    >
      <div className="relative w-full select-none">
        <NowYouTry show={demo.done && !reduced && !hasCue} />

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          {/* Beaker + dispersed solute */}
          <div className="flex flex-shrink-0 justify-center">
            <svg
              viewBox={`0 0 ${W} ${H}`}
              className="w-40"
              role="img"
              aria-label={`${descriptor} solution of ${solute}: ${fmt(molarity)} molar, ${fmt(moles)} moles in ${fmt(volume)} liters`}
            >
              {/* Solution tint — alpha grows with concentration. */}
              <rect
                x={IN_X}
                y={IN_TOP}
                width={IN_W}
                height={IN_BOTTOM - IN_TOP}
                rx="3"
                fill={soluteColor}
                fillOpacity={tintAlpha}
                style={{ transition }}
              />
              {/* Surface line of the solvent. */}
              <rect x={IN_X} y={IN_TOP} width={IN_W} height="3" fill={soluteColor} fillOpacity={Math.min(0.6, tintAlpha + 0.25)} style={{ transition }} />

              {/* Dispersed solute particles (prefix of the stable field). */}
              <g>
                {field.map((p, i) => {
                  const shown = i < visible;
                  return (
                    <circle
                      key={i}
                      cx={p.x}
                      cy={p.y}
                      r={spotlight ? p.r + 0.8 : p.r}
                      fill={soluteColor}
                      stroke={spotlight ? 'var(--ws-accent)' : 'rgba(0,0,0,0.28)'}
                      strokeWidth={spotlight ? 1.4 : 0.8}
                      style={{ transition, opacity: shown ? 1 : 0 }}
                    />
                  );
                })}
              </g>

              {/* Beaker glass: drawn last so it sits in front of the solution. */}
              <path
                d={`M22 18 L22 ${IN_BOTTOM} Q22 210 34 210 L166 210 Q178 210 178 ${IN_BOTTOM} L178 18`}
                fill="none"
                stroke="var(--ws-border-strong)"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              {/* Pour lip. */}
              <path d="M22 18 L14 12" fill="none" stroke="var(--ws-border-strong)" strokeWidth="2.5" strokeLinecap="round" />
              {/* Graduation marks. */}
              {[0.25, 0.5, 0.75].map((f) => {
                const y = IN_BOTTOM - f * (IN_BOTTOM - IN_TOP);
                return <line key={f} x1={IN_X} y1={y} x2={IN_X + 10} y2={y} stroke="var(--ws-border)" strokeWidth="1.5" />;
              })}
            </svg>
          </div>

          {/* Readout + control */}
          <div className="min-w-0 flex-1">
            {/* Live readout. */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-md border border-ws-accent bg-ws-accent-quiet px-2 py-2">
                <div className="text-[10px] uppercase tracking-wide text-ws-accent-ink">Molarity</div>
                <div className="font-mono text-lg font-bold leading-tight text-ws-accent-ink">{fmt(molarity)}</div>
                <div className="text-[10px] text-ws-accent-ink">mol/L</div>
              </div>
              <div className="rounded-md border border-ws-border bg-ws-surface-2 px-2 py-2">
                <div className="text-[10px] uppercase tracking-wide text-ws-ink-3">Moles</div>
                <div className="font-mono text-lg font-bold leading-tight text-ws-ink">{fmt(moles)}</div>
                <div className="text-[10px] text-ws-ink-3">mol solute</div>
              </div>
              <div className="rounded-md border border-ws-border bg-ws-surface-2 px-2 py-2">
                <div className="text-[10px] uppercase tracking-wide text-ws-ink-3">Volume</div>
                <div className="font-mono text-lg font-bold leading-tight text-ws-ink">{fmt(volume)}</div>
                <div className="text-[10px] text-ws-ink-3">L solution</div>
              </div>
            </div>

            {/* Concentration slider: concentrated ↔ dilute. */}
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs">
                <span className="text-ws-ink-2">Concentration</span>
                <span className="font-mono font-semibold capitalize tabular-nums text-ws-ink">{descriptor}</span>
              </div>
              <input
                type="range"
                min={M_MIN}
                max={M_MAX}
                step={M_STEP}
                value={molarity}
                onPointerDown={cancelDemo}
                onKeyDown={cancelDemo}
                onChange={(e) => { setMolarity(clampM(parseFloat(e.target.value))); mark(); }}
                className="w-full accent-ws-accent"
                aria-label="Concentration in moles per liter"
                aria-valuetext={`${fmt(molarity)} molar (${descriptor})`}
              />
              <div className="flex justify-between text-[10px] text-ws-ink-3">
                <span>dilute</span>
                <span>concentrated</span>
              </div>
            </div>

            {/* The defining relationship, with the live numbers. */}
            <div className="mt-3 rounded-md border border-ws-border bg-ws-surface-2 px-3 py-2 text-center font-mono text-xs text-ws-ink-2">
              M = <span className="text-ws-ink">{fmt(moles)} mol</span> ÷ <span className="text-ws-ink">{fmt(volume)} L</span> = <span className="font-semibold text-ws-accent">{fmt(molarity)} M</span>
            </div>
          </div>
        </div>

        <p className="mt-2 text-xs text-ws-ink-3">
          Molarity counts dissolved <span className={spotlight ? 'rounded px-1 font-semibold text-ws-accent' : 'font-medium text-ws-ink-2'}>{solute}</span> particles per liter of solution. The amount of {solute} doesn't change as you stir — only how crowded it is in the same space.
        </p>
      </div>
    </VizCard>
  );
}
